import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const ABRIR = { type: 'spring', stiffness: 240, damping: 32 };

/**
 * Espacio que se abre cuando su contenido aparece y se cierra cuando se va.
 *
 * Los widgets de Inicio cargan cada uno por su cuenta y muchos solo se muestran
 * si hay algo pendiente (un ticket, un taxi, una encuesta). Sin esto, cada uno
 * que llega empuja de golpe todo lo que tiene debajo. Aqui el espacio crece con
 * un resorte y el widget aparece con un fundido; al descartarlo, el hueco se
 * cierra igual de suave. Si el contenido cambia de alto, tambien acompaña.
 *
 * espacio: aire debajo del contenido cuando esta visible (el gap de la columna).
 * noAntesDe: marca de tiempo (ms) antes de la cual no se abre; asi un widget
 * que carga rapido no se adelanta a la secuencia de entrada de la pagina.
 * abrirDesdeCero: en false, el primer alto se toma tal cual (el contenido ya
 * entra con su propia animacion) y solo se animan los cambios posteriores.
 */
const Aparecer = ({ children, espacio = 24, noAntesDe = 0, abrirDesdeCero = true }) => {
  const reduce = useReducedMotion();
  const contenido = useRef(null);
  const [alto, setAlto] = useState(0);
  const [retardo, setRetardo] = useState(0);
  const [animando, setAnimando] = useState(false);
  const visibleAntes = useRef(false);
  const [listo, setListo] = useState(abrirDesdeCero);

  useLayoutEffect(() => {
    const el = contenido.current;
    if (!el) return undefined;
    const medir = () => {
      const h = el.offsetHeight;
      // Primera vez que hay algo: esperar a que le toque en la secuencia.
      if (h > 0 && !visibleAntes.current) {
        visibleAntes.current = true;
        setRetardo(Math.max(0, (noAntesDe - Date.now()) / 1000));
      }
      if (h === 0) visibleAntes.current = false;
      setAlto(h);
      // El primer alto se aplica sin animar; desde el cuadro siguiente, todo anima.
      if (h > 0) requestAnimationFrame(() => setListo(true));
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [noAntesDe]);

  const visible = alto > 0;
  return (
    <motion.div
      initial={false}
      animate={{ height: visible ? alto + espacio : 0, opacity: visible ? 1 : 0 }}
      transition={reduce || !listo
        ? { duration: 0 }
        : { height: { ...ABRIR, delay: retardo }, opacity: { duration: 0.35, delay: retardo + 0.06 } }}
      onAnimationStart={() => setAnimando(true)}
      onAnimationComplete={() => setAnimando(false)}
      // Solo recorta mientras se abre o se cierra: quieto, deja ver sombras y menus.
      style={{ overflow: animando || !visible ? 'hidden' : 'visible' }}
    >
      {/* flow-root: los margenes del widget cuentan en el alto medido. */}
      <div ref={contenido} style={{ display: 'flow-root' }}>{children}</div>
    </motion.div>
  );
};

export default Aparecer;
